import { State } from "xstate";
import { CartSelectors } from "@capone/checkout";
import {
  BookingQuestion,
  BookingQuestionAnswer,
  BookingQuestionId,
  ExperienceBookableItem,
  ExperiencesAgeBandEnum,
  ActivityPriceBreakdown,
  ActivityShopResponse,
  IExperiencesPassengerCounts,
  Person,
} from "redmond";
import { Product } from "@b2bportal/purchase-api";
import dayjs from "dayjs";

import { ActivityMachineContext } from "../types";
import {
  BookingQuestionResponse,
  ActivityAdditionalDetailsSelectors,
  ActivityTravelerSelectors,
  HOTEL_OPTION_NOT_AVAILABLE_LABEL,
  PickupPointOption,
} from "../../../../checkout";
import { getDistinctAgeBands } from "../../../../checkout/states/TravelerInformation/selectors";

type ActivityStateType = State<ActivityMachineContext>;
type ActivityStateWithoutValue = Pick<ActivityStateType, "context">;

export const getActivityShop = (
  state: ActivityStateWithoutValue
): ActivityShopResponse | undefined => state.context.activityShop;

export const getSelectedBookableItem = (
  state: ActivityStateWithoutValue
): ExperienceBookableItem | null | undefined => state.context.selectedBookableItem;

export const getActivityTravelerCounts = (
  state: ActivityStateWithoutValue
): IExperiencesPassengerCounts | undefined => state.context.travelerCounts;

export const getActivityPriceBreakdown = (
  state: ActivityStateWithoutValue
): ActivityPriceBreakdown | undefined => {
  const quoteBreakdown = CartSelectors.getQuoteBreakdown(state);
  const quotedActivityProduct = quoteBreakdown?.products.find(
    (product) => product.product.type === ("Experiences" as Product)
  );

  const quotedActivityProductValue = quotedActivityProduct?.product.value
    .breakdown as ActivityPriceBreakdown | undefined;

  // adding date to quote price breakdown as well.
  if (quotedActivityProductValue && state.context?.priceBreakdown?.date) {
    quotedActivityProductValue.date = state.context.priceBreakdown.date;
    quotedActivityProductValue.opaqueShoppedProduct =
      state.context.priceBreakdown.opaqueShoppedProduct;
  }

  return quotedActivityProductValue || state.context.priceBreakdown;
};

const getTravelersAnswerPerQuestion = (
  id: BookingQuestionId,
  travelers: Person[],
  responses?: BookingQuestionResponse,
  ageBands?: {
    ageBand: ExperiencesAgeBandEnum;
    lowerBound: number;
    upperBound: number;
  }[]
) =>
  travelers.map((traveler, idx) => {
    const { weight = "", heightFt, heightIn } = responses?.[traveler.id] || {};

    let answer = "";
    let unit = "";

    switch (id) {
      case BookingQuestionId.Height: {
        answer = `${heightFt}'${heightIn}`;
        unit = "ft";
        break;
      }
      case BookingQuestionId.Weight: {
        answer = weight;
        unit = "lbs";
        break;
      }
      case BookingQuestionId.FullNamesFirst: {
        answer = traveler.givenName;
        break;
      }
      case BookingQuestionId.FullNamesLast: {
        answer = traveler.surname;
        break;
      }
      case BookingQuestionId.Ageband: {
        answer = "TRAVELER"; // default to Traveler
        if (ageBands && ageBands.length > 0) {
          const age = dayjs().diff(dayjs(traveler.dateOfBirth), "year");

          ageBands.forEach(({ upperBound, lowerBound, ageBand }) => {
            if (age >= lowerBound && age <= upperBound) {
              answer = ageBand.toUpperCase();
            }
          });
        }
        break;
      }
      case BookingQuestionId.DateOfBirth: {
        answer = dayjs(traveler.dateOfBirth).format("DD-MM-YYYY");
        break;
      }
      case BookingQuestionId.PassportPassportNo: {
        answer =
          traveler.passport?.number && traveler.passport.number.trim()
            ? traveler.passport.number
            : "";
        break;
      }
      case BookingQuestionId.PassportExpiry: {
        answer =
          traveler.passport?.expiration &&
          traveler.passport.expiration.trim()
            ? traveler.passport.expiration
            : "";
        break;
      }
      case BookingQuestionId.PassportNationality: {
        answer =
          traveler.passport?.countryOfIssue &&
          traveler.passport.countryOfIssue.trim()
            ? traveler.passport.countryOfIssue
            : "";
        break;
      }
      default:
    }

    return unit
      ? {
          bookingQuestionId: id,
          travelerNum: idx + 1,
          unit,
          answer,
        }
      : {
          bookingQuestionId: id,
          travelerNum: idx + 1,
          answer,
        };
  });

export const getBookingQuestionAnswers = ({
  context,
}: ActivityStateWithoutValue): BookingQuestionAnswer[] => {
  const perTravelerBookingQuestions =
    context.activityShop?.bookingQuestions?.perTraveler || [];
  const perBookingBookingQuestions =
    context.activityShop?.bookingQuestions?.perBooking || [];

  const distinctAgeBands = getDistinctAgeBands({ context });
  const travelers = ActivityTravelerSelectors.getAllSelectedUserTravelers({
    context,
  });

  const {
    selectedPickupPoint,
    hotelSelection,
    hotelName,
    address,
    city,
    zipCode,
  } = ActivityAdditionalDetailsSelectors.getPickupPointStates({ context });
  const specialRequirement =
    ActivityAdditionalDetailsSelectors.getSpecialRequirement({ context });

  const travelerBookingAnswers: BookingQuestionAnswer[] = [];
  if (perTravelerBookingQuestions.length > 0) {
    perTravelerBookingQuestions.forEach(({ id }) => {
      const answers = getTravelersAnswerPerQuestion(
        id,
        travelers,
        context.activityTravelerInformation.bookingQuestionResponse,
        distinctAgeBands
      );
      travelerBookingAnswers.push(...answers);
    });
  }

  let pickupPointAnswer: BookingQuestionAnswer[] = [];
  if (
    selectedPickupPoint === PickupPointOption.SelectPickup &&
    hotelSelection
  ) {
    if (
      hotelSelection?.experienceLocation?.name ===
      HOTEL_OPTION_NOT_AVAILABLE_LABEL
    ) {
      pickupPointAnswer = [
        {
          bookingQuestionId: BookingQuestionId.PickupPoint,
          answer: [hotelName, address, city, zipCode].join(", "),
          unit: "FREETEXT",
        },
      ];
    } else {
      pickupPointAnswer = [
        {
          bookingQuestionId: BookingQuestionId.PickupPoint,
          answer: hotelSelection.locationRef,
          unit: "LOCATION_REFERENCE",
        },
      ];
    }
  } else if (selectedPickupPoint === PickupPointOption.PickupLater) {
    pickupPointAnswer = [
      {
        bookingQuestionId: BookingQuestionId.PickupPoint,
        answer: "CONTACT_SUPPLIER_LATER",
        unit: "LOCATION_REFERENCE",
      },
    ];
  } else if (selectedPickupPoint === PickupPointOption.NoPickup) {
    pickupPointAnswer = [
      {
        bookingQuestionId: BookingQuestionId.PickupPoint,
        answer: "MEET_AT_DEPARTURE_POINT",
        unit: "LOCATION_REFERENCE",
      },
    ];
  }

  return [
    specialRequirement && {
      bookingQuestionId: BookingQuestionId.SpecialRequirements,
      answer: specialRequirement,
    },
    perBookingBookingQuestions.find(
      ({ id }: BookingQuestion) => id === BookingQuestionId.TransferArrivalMode
    ) && {
      bookingQuestionId: BookingQuestionId.TransferArrivalMode,
      answer: "OTHER",
    },
    perBookingBookingQuestions.find(
      ({ id }: BookingQuestion) =>
        id === BookingQuestionId.TransferDepartureMode
    ) && {
      bookingQuestionId: BookingQuestionId.TransferDepartureMode,
      answer: "OTHER",
    },
    ...pickupPointAnswer,
    ...travelerBookingAnswers,
  ].filter((answer) => !!answer) as BookingQuestionAnswer[];
};
